import {
  AutomationKindType,
  type AudienceFilterInputType,
  type AutomationInputType,
  type AutomationNodeInputType,
} from "@/types/graphql/partner.generated";
import {
  emptyAudience,
  type AutomationEditorDetails,
  type AutomationEditorNode,
} from "@/partner/entities/marketing";
import {
  NODE_STEP_Y,
  flowNode,
  type AutomationFormState,
  type AutomationNodeData,
  type FlowNode,
} from "./graphTypes";
import { fromExitRules, toExitRuleInput } from "./exitRulesForm";
import { toFilterInput } from "./filterInput";
import { NODE_TYPE_TABLE, nodeTypeSpec } from "./nodeTypeTable";

/**
 * Перевод графа между ответом сервера и состоянием редактора: чтение
 * автоматизации, вход сохранения и проверки, снимок для «есть ли
 * несохранённые правки», узлы, которые эта версия кабинета не понимает.
 */

/**
 * Названия шаблонов писем из сохранённого графа: подписи шагов «Письмо».
 * Берёт только `graph` (не всю автоматизацию, Минор 7 ре-ревью): вызывающий
 * код может держать зависимость useMemo на `automation.graph`, а не на
 * всей автоматизации, и не пересчитывать карту при каждом опросе
 * статистики активной автоматизации.
 */
export function emailTemplateNames(
  graph: AutomationEditorDetails["graph"]
): Map<string, string> {
  const names = new Map<string, string>();
  for (const node of graph.nodes) {
    const template = node.sendEmail?.emailTemplate;
    if (template) names.set(template.id, template.name);
  }
  return names;
}

/**
 * Поля узла, которые модель понимает сама (форма ответа
 * AutomationEditorQuery: id/type/position/trigger/sendEmail плюс
 * __typename на каждом уровне). Любое другое поле в ответе сервера
 * сегодня невозможно (кабинет получает только то, что сам запросил), но
 * это ровно тот сигнал, что кабинет отстал от схемы (фаза 4): такой узел
 * помечается unsupportedConfig, а не пробрасывается вслепую (I1/I2).
 */
const KNOWN_NODE_FIELDS = new Set([
  "id",
  "type",
  "position",
  "trigger",
  "sendEmail",
  // Фаза 4: фрагмент запрашивает конфиги WAIT и CONDITION у каждого узла;
  // у остальных типов они пустые, у самих WAIT и CONDITION их читают
  // записи NODE_TYPE_TABLE.
  "wait",
  "condition",
  "__typename",
]);

function nodeDataFromServer(node: AutomationEditorNode): AutomationNodeData {
  const spec = NODE_TYPE_TABLE.get(node.type);
  const hasUnknownFields = Object.keys(node).some(
    (key) => !KNOWN_NODE_FIELDS.has(key)
  );
  return {
    type: node.type,
    trigger: null,
    sendEmail: null,
    wait: null,
    condition: null,
    ...(spec ? spec.fromServer(node) : {}),
    ...(!spec || hasUnknownFields ? { unsupportedConfig: true } : {}),
  };
}

/**
 * Узлы, чей тип или конфиг кабинет не понимает: нет записи в
 * NODE_TYPE_TABLE, или запись есть, но серверные данные несли поле,
 * которого её fromServer не читает. Редактор должен уйти в read-only
 * (или заблокировать сохранение) и показать уведомление, пока список не
 * пуст: toAutomationInput на таком состоянии бросает
 * UnsupportedAutomationNodesError, а не угадывает конфиг. serializeForm
 * при этом не бросает (см. её описание): чтение снимка для
 * dirty-tracking и попытка сохранить это разные операции.
 */
export function unsupportedNodeIds(state: AutomationFormState): string[] {
  return state.nodes
    .filter(
      (node) =>
        !NODE_TYPE_TABLE.has(node.data.type) ||
        node.data.unsupportedConfig === true
    )
    .map((node) => node.id);
}

/**
 * true, если в графе есть хотя бы один узел из unsupportedNodeIds.
 * Единственный предикат, по которому редактор решает уйти в read-only и
 * показать уведомление вместо формы: сохранение (toAutomationInput) и
 * dirty-tracking (serializeForm) сами по себе не бросают на такое
 * состояние без явной проверки этим предикатом на стороне UI.
 */
export const isReadOnly = (state: AutomationFormState): boolean =>
  unsupportedNodeIds(state).length > 0;

/** Брошено toAutomationInput, пока в графе есть узел из unsupportedNodeIds. */
export class UnsupportedAutomationNodesError extends Error {
  readonly nodeIds: string[];

  constructor(nodeIds: string[]) {
    super(`Automation graph has unsupported node(s): ${nodeIds.join(", ")}`);
    this.name = "UnsupportedAutomationNodesError";
    this.nodeIds = nodeIds;
  }
}

/**
 * Состояние редактора из автоматизации. Узел неизвестного типа или с
 * неизвестным конфигом остаётся в состоянии со своим id, типом и
 * позицией (полотно рисует его как «не поддерживается»), но без
 * настроек: их кабинет прочитать не умеет (см. unsupportedNodeIds).
 */
export function fromAutomation(
  automation: AutomationEditorDetails
): AutomationFormState {
  const audience: AudienceFilterInputType = automation.audience
    ? toFilterInput(automation.audience)
    : emptyAudience();
  return {
    kind: automation.kind,
    name: automation.name,
    viewersEnabled: Boolean(automation.audience),
    audience,
    contactListIds: [...automation.contactListIds],
    // Узел без сохранённой позиции (шаг из шаблона до первого перетаскивания)
    // раскладывается по вертикали здесь же, в памяти редактора: открытие
    // такой автоматизации не считается правкой (fromAutomation дважды даёт
    // одинаковый снимок), но первое сохранение без единого движения мышью
    // запишет эти вычисленные координаты как настоящие (M1, минорный:
    // шаблоны всегда задают позиции, случай редкий).
    nodes: automation.graph.nodes.map((node, index) =>
      flowNode(
        node.id,
        nodeDataFromServer(node),
        node.position
          ? { x: node.position.x, y: node.position.y }
          : { x: 0, y: index * NODE_STEP_Y }
      )
    ),
    edges: automation.graph.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle ?? null,
    })),
    exitRules: fromExitRules(automation.exitRules),
    reentry: {
      mode: automation.reentry.mode,
      cooldownDays: automation.reentry.cooldownDays,
    },
    // Выбор для удалённых шагов живёт до сохранения: после успешной записи
    // useAutomationLifecycle очищает его (clearNodeMoves), иначе следующее
    // сохранение послало бы выбор для шага, которого на сервере уже нет.
    nodeMoves: [],
  };
}

/**
 * Позиция для входа: округление до целых и, если передан предел сервера
 * (AutomationGraphLimits.positionAbs), прижатие к нему по модулю.
 */
function inputPosition(
  position: { x: number; y: number },
  positionAbs: number | null
): { x: number; y: number } {
  const clamp = (value: number): number => {
    const rounded = Math.round(value);
    return positionAbs === null
      ? rounded
      : Math.max(-positionAbs, Math.min(positionAbs, rounded));
  };
  return { x: clamp(position.x), y: clamp(position.y) };
}

/**
 * Условия аудитории в каноническом виде (тот же порядок ключей и
 * `?? null`, что строит fromAutomation): аудитория меняется своим
 * редактором (features/audience), и его форма объекта не гарантирована
 * побайтово, а serializeForm обязан не считать это правкой, если сами
 * значения не изменились.
 */
function canonicalAudience(
  filter: AudienceFilterInputType
): AudienceFilterInputType {
  return {
    match: filter.match,
    conditions: filter.conditions.map((condition) => ({
      field: condition.field,
      op: condition.op,
      boolValue: condition.boolValue ?? null,
      numberValue: condition.numberValue ?? null,
      stringValues: condition.stringValues ?? null,
      // Шаг письма бывает только у условий шага CONDITION: у аудитории
      // ключа нет, как во входе фазы 3.
      ...(condition.nodeId ? { nodeId: condition.nodeId } : {}),
    })),
  };
}

/**
 * Поля цепочки во входе: правила выхода, повторный вход и выбор для
 * зрителей удалённых шагов. У остальных видов этих ключей нет совсем:
 * вход рассылки и автоматизации по событию побайтово тот же, что в фазе 3
 * (сервер их не меняет, если не переданы), и не зависит от того, что
 * осталось в состоянии. Выбор уходит только для шагов, которых в графе
 * формы нет: шаг, чьё удаление отменили, снова в графе, и выбор для него
 * сервер отверг бы.
 */
function journeyInput(
  state: AutomationFormState
): Pick<AutomationInputType, "exitRules" | "reentry" | "nodeMoves"> {
  if (state.kind !== AutomationKindType.JourneyType) return {};
  const present = new Set(state.nodes.map((n) => n.id));
  return {
    exitRules: state.exitRules.map(toExitRuleInput),
    reentry: {
      mode: state.reentry.mode,
      cooldownDays: state.reentry.cooldownDays,
    },
    nodeMoves: state.nodeMoves
      .filter((m) => !present.has(m.nodeId))
      .map((m) => ({ nodeId: m.nodeId, action: m.action })),
  };
}

/** Вход мутации для узла его типа: id/type/position плюс то, что вернёт NodeTypeSpec.toInput (I1). */
function supportedNodeInput(
  node: FlowNode,
  position: { x: number; y: number } | null
): AutomationNodeInputType {
  return {
    id: node.id,
    type: node.data.type,
    position,
    trigger: null,
    sendEmail: null,
    // wait и condition только у своих типов (NodeTypeSpec.toInput): у
    // событий и писем вход тот же, что в фазе 3.
    ...nodeTypeSpec(node.data.type).toInput(node.data),
  };
}

/**
 * Вход сохранения: граф формы плюс версия, от которой начата правка
 * (финальное ревью I2). Сервер пишет только если версия совпала, иначе
 * AUTOMATION_REVISION_CONFLICT без записи. positionAbs: предел координаты
 * из правил сервера, null (правил нет) только округляет.
 */
export function toSaveInput(
  state: AutomationFormState,
  expectedRevision: number,
  positionAbs: number | null
): AutomationInputType {
  return { ...toAutomationInput(state, { positionAbs }), expectedRevision };
}

/**
 * Вход мутации и проверки графа. positions: false для проверки: сдвиг
 * шага мышью не должен запускать запрос ошибок. Каждый узел строится
 * только из id/type/position и того, что вернёт NodeTypeSpec.toInput
 * своего типа (I1): никакое другое поле data, включая служебные поля UI
 * вроде подписи или подсветки ошибок, в мутацию не попадает. Бросает,
 * пока в графе есть узел из unsupportedNodeIds: отправлять его конфиг
 * (никакого нет) значило бы молча стереть настройки партнёра (I2). Это
 * единственная функция, которая бросает; вызывать её нужно только на
 * сохранении и на явной проверке графа, не на каждый рендер (для этого
 * есть serializeForm, которая не бросает никогда).
 */
export function toAutomationInput(
  state: AutomationFormState,
  options: { positions?: boolean; positionAbs?: number | null } = {}
): AutomationInputType {
  const unsupported = unsupportedNodeIds(state);
  if (unsupported.length > 0)
    throw new UnsupportedAutomationNodesError(unsupported);
  const withPositions = options.positions ?? true;
  const positionAbs = options.positionAbs ?? null;
  return {
    name: state.name.trim(),
    audience: state.viewersEnabled ? canonicalAudience(state.audience) : null,
    contactListIds: state.contactListIds,
    graph: {
      nodes: state.nodes.map((node) =>
        supportedNodeInput(
          node,
          withPositions ? inputPosition(node.position, positionAbs) : null
        )
      ),
      edges: state.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle ?? null,
      })),
    },
    ...journeyInput(state),
  };
}

/**
 * Проекция состояния для сравнения снимков (dirty-tracking), никогда не
 * бросает. Для поддерживаемого узла это то же, что уйдёт в
 * toAutomationInput (id/type/position плюс NodeTypeSpec.toInput), с
 * позицией, округлённой без прижатия к пределу: прижатие делает только
 * вход сохранения. Для узла из unsupportedNodeIds это только
 * id/type/position: его настройки кабинет прочитать не умеет и
 * редактировать их нельзя (форма для него не рисуется), так что менять их
 * и, значит, помечать форму грязной из-за них тоже нечему. Именно поэтому
 * serializeForm может не бросать на неподдерживаемом узле: открыть
 * автоматизацию с таким узлом это не попытка её сохранить. Правила выхода,
 * повторный вход и выбор для удалённых шагов входят в снимок так же, как
 * во вход мутации: их правка тоже правка.
 */
function toComparable(state: AutomationFormState): unknown {
  const unsupported = new Set(unsupportedNodeIds(state));
  return {
    name: state.name.trim(),
    audience: state.viewersEnabled ? canonicalAudience(state.audience) : null,
    contactListIds: state.contactListIds,
    graph: {
      nodes: state.nodes.map((node) => {
        const position = inputPosition(node.position, null);
        if (unsupported.has(node.id)) {
          return { id: node.id, type: node.data.type, position };
        }
        return supportedNodeInput(node, position);
      }),
      edges: state.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle ?? null,
      })),
    },
    ...journeyInput(state),
  };
}

/**
 * Снимок для «есть ли несохранённые правки» (выделение, viewport и
 * размеры узлов не входят, потому что toComparable их не читает).
 * Никогда не бросает, в том числе на графе с неподдерживаемым узлом
 * (см. toComparable): это функция, которую компонент редактора вызывает
 * на каждый рендер через useMemo, а не только на сохранении, так что она
 * не может падать на состоянии, которое fromAutomation в принципе
 * строит. Решение "можно ли сохранять" принимает isReadOnly/
 * unsupportedNodeIds и отдельно toAutomationInput на самом сохранении.
 */
export const serializeForm = (state: AutomationFormState): string =>
  JSON.stringify(toComparable(state));
